"use client";

import { useEffect, useRef, type CSSProperties } from "react";
import type { VideoPlayback } from "./model";

/**
 * A video file in a row, its picture the poster until it plays (the shared player shows a poster only on iOS). It
 * starts by itself only muted and never for a visitor who has turned motion off, so the attribute is left out and
 * playback starts here; a film that cannot start by itself always shows its controls.
 */
export default function RecommendationVideo({
  url,
  poster,
  position,
  playback,
  title,
}: {
  url: string;
  poster?: string;
  position?: string;
  playback: VideoPlayback;
  title: string;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const autoplay = playback.autoplay && playback.muted;
  useEffect(() => {
    const element = video.current;
    if (!element || !autoplay) return;
    // React sets `muted` as a property only, and browsers refuse to start a film with sound.
    element.muted = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    element.play().catch(() => {});
  }, [autoplay, url]);
  return (
    <video
      ref={video}
      className="pf-image pf-video"
      src={url}
      poster={poster}
      title={title}
      controls={playback.controls || !autoplay}
      muted={playback.muted}
      loop={playback.loop}
      playsInline
      preload={poster ? "none" : "metadata"}
      style={{
        objectPosition: position,
        objectFit: "var(--pf-image-fit,cover)" as CSSProperties["objectFit"],
      }}
    />
  );
}
